import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { useEffect } from "react";
import { linkFiles, useUpload } from "@/api/files";
import { useAuth } from "@/hooks/use-auth";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { collectDroppedFiles, isFileDrag } from "./dropped-files";
import {
	resolveTicketDropTarget,
	TICKET_DROP_STATE_ATTRIBUTE,
	type TicketDropTarget,
} from "./resolve-ticket-drop-target";

/**
 * Attaches files dragged in from the desktop to the ticket card they are dropped on, by the same
 * two steps the card's paperclip runs: upload the bytes, then link the results into `ticketFiles`.
 *
 * **One instance serves every card on the page**, and that is the point of it. A dropzone per card
 * puts four document listeners and a re-render on each of them for every drag event the document
 * sees — a board rendering three hundred cards then spends a drag re-rendering three hundred
 * components, several times a second, including while a ticket is being dragged between crews. Here
 * the card under the cursor is found by walking up from the event's target, and the affordance is
 * one attribute toggled on that element, so no card renders at all while a drag is in flight.
 *
 * Mounted once beside the other signed-in surfaces in `app.tsx`. Renders nothing.
 */
export function TicketFileDrop() {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const { organization } = useAuth();
	const { updateIsDenied } = useTicketRules();
	const { uploadFn } = useUpload();

	/** Effects */
	useEffect(() => {
		/** The card the state attribute sits on, so it comes off exactly where it was put. */
		let activeElement: HTMLElement | null = null;

		function markActive(element: HTMLElement | null) {
			if (activeElement === element) return;
			activeElement?.removeAttribute(TICKET_DROP_STATE_ATTRIBUTE);
			element?.setAttribute(TICKET_DROP_STATE_ATTRIBUTE, "active");
			activeElement = element;
		}

		/**
		 * Each arm is a way the link route would refuse the row, asked before the card lights up so
		 * a drop is never offered where it would fail on release.
		 */
		function isAttachable(target: TicketDropTarget) {
			// A ticket's files follow the ticket: the fixed ticket rule is what the link route asks,
			// and the one every other edit surface of the card asks.
			if (updateIsDenied({ id: target.ticketId, projectId: target.projectId })) return false;
			// A guest board renders the host's tickets, and an administrator's wildcard answers the
			// ticket rule for a foreign project too — so the row's own organisation is asked
			// separately. `ticketFiles` is written with the caller's claims, which no rule widens.
			return !!organization?.id && target.orgId === organization.id;
		}

		/**
		 * The card under the drag, or null. The rules are asked only where the drag crosses onto a
		 * different card: dragover fires with every frame of one, and the card it is already
		 * standing on was cleared when it got there.
		 */
		function resolveAttachableElement(event: DragEvent) {
			const target = resolveTicketDropTarget(event.target);
			if (!target) return null;
			if (target.element === activeElement) return activeElement;
			return isAttachable(target) ? target.element : null;
		}

		/**
		 * Uploads the dropped files and links them to the ticket, reporting the transfer in a toast.
		 * The toast is where the progress lives rather than the card, so scrolling the board away
		 * from a ticket does not take the only sign of a running upload with it.
		 */
		function attachFiles(target: TicketDropTarget, files: File[]) {
			if (!files.length) return;
			const form = new FormData();
			for (const file of files) form.append("files", file);
			toast.promise(
				async () => {
					const uploadResults = await uploadFn(form);
					if (!uploadResults?.length) throw new Error("The upload answered with no results");
					await linkFiles(
						"ticketFiles",
						{ ticketId: target.ticketId, projectId: target.projectId },
						{ uploadResults },
					);
					await qc.invalidateQueries({
						predicate: invalidate(["tickets", target.ticketId, target.projectId]),
					});
				},
				{
					// The counted key has no singular form, and one file is the common drop.
					loading:
						files.length === 1
							? t("file_upload.uploading")
							: t("file_upload.n_files_uploading", { count: `${files.length}` }),
					success: t("file_upload.files_uploaded_successfully"),
					error: t("file_upload.upload_failed"),
				},
			);
		}

		function handleOnDragOver(event: DragEvent) {
			// A ticket being dragged across a board reaches this listener too, and leaves it here:
			// everything below is for a drag carrying files, and the board's own drag and drop is
			// not to be touched.
			if (!isFileDrag(event.dataTransfer)) return;
			const element = resolveAttachableElement(event);
			markActive(element);
			if (!element) return;
			// The default has to be prevented on every dragover, not once: that is what makes the
			// browser release the file onto the card rather than opening it in a tab of its own.
			event.preventDefault();
			if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
		}

		function handleOnDrop(event: DragEvent) {
			markActive(null);
			if (!isFileDrag(event.dataTransfer)) return;
			const target = resolveTicketDropTarget(event.target);
			if (!target || !isAttachable(target)) return;
			event.preventDefault();
			attachFiles(target, collectDroppedFiles(event.dataTransfer));
		}

		function handleOnDragLeave(event: DragEvent) {
			// A dragleave that enters nothing is the pointer leaving the window. Every other one is
			// the drag crossing between elements, which the dragover right behind it answers.
			if (!event.relatedTarget) markActive(null);
		}

		function handleOnDragEnd() {
			markActive(null);
		}

		document.addEventListener("dragover", handleOnDragOver);
		document.addEventListener("drop", handleOnDrop);
		document.addEventListener("dragleave", handleOnDragLeave);
		document.addEventListener("dragend", handleOnDragEnd);

		return () => {
			markActive(null);
			document.removeEventListener("dragover", handleOnDragOver);
			document.removeEventListener("drop", handleOnDrop);
			document.removeEventListener("dragleave", handleOnDragLeave);
			document.removeEventListener("dragend", handleOnDragEnd);
		};
	}, [t, qc, organization?.id, updateIsDenied, uploadFn]);

	/** Render */
	return null;
}
